/** The server's limit for a clinic logo (bytes). */
export const LOGO_MAX_BYTES = 180 * 1024;
const LOGO_MAX_SIDE = 512;

export type EncodedImage = { type: 'image/png' | 'image/webp'; base64: string; bytes: number };

function encode(
  canvas: HTMLCanvasElement,
  type: EncodedImage['type'] | 'image/jpeg',
  quality?: number
) {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), type, quality)
  );
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/**
 * The image drawn with its longest side at most `maxSide` (never enlarged),
 * over `background` when given (JPEG has no transparency).
 */
function drawScaled(bitmap: ImageBitmap, maxSide: number, background?: string): HTMLCanvasElement {
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (ctx && background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * Scales the logo down on the device (longest side 512 px) and keeps
 * transparency: PNG, or WebP when the PNG is still too large.
 */
export async function resizeLogo(file: Blob): Promise<EncodedImage> {
  const bitmap = await createImageBitmap(file);
  const canvas = drawScaled(bitmap, LOGO_MAX_SIDE);
  bitmap.close();

  let type: EncodedImage['type'] = 'image/png';
  let blob = await encode(canvas, type);
  if (blob.size > LOGO_MAX_BYTES) {
    type = 'image/webp';
    blob = await encode(canvas, type, 0.85);
  }
  if (blob.size > LOGO_MAX_BYTES) throw new Error('logo too large');
  return { type, base64: await toBase64(blob), bytes: blob.size };
}

/** What the server accepts for one X-ray (bytes); the device aims well below it. */
export const XRAY_MAX_BYTES = 1024 * 1024;
/** Target size of a stored X-ray. */
export const XRAY_TARGET_BYTES = 400 * 1024;

/** Longest side and JPEG quality, tried in order until the image is small enough. */
export const XRAY_STEPS: readonly { side: number; quality: number }[] = [
  { side: 2000, quality: 0.82 },
  { side: 2000, quality: 0.72 },
  { side: 1600, quality: 0.72 },
  { side: 1600, quality: 0.6 },
  { side: 1280, quality: 0.6 },
];

/**
 * The first encoding within `target`, or the smallest one tried. `encode` is
 * passed in so the choice can be tested without a canvas.
 */
export async function shrinkToTarget<T extends { blob: Blob }>(
  encode: (step: { side: number; quality: number }) => Promise<T>,
  target = XRAY_TARGET_BYTES,
  steps = XRAY_STEPS
): Promise<T> {
  let smallest: T | null = null;
  for (const step of steps) {
    const result = await encode(step);
    if (result.blob.size <= target) return result;
    if (!smallest || result.blob.size < smallest.blob.size) smallest = result;
  }
  if (!smallest) throw new Error('no encoding steps');
  return smallest;
}

export type CompressedImage = { blob: Blob; width: number; height: number };

/**
 * An X-ray photo or scan made small on the device before it is sent: JPEG,
 * longest side at most 2000 px, a few hundred KB. Drawing it on a canvas also
 * drops the original's metadata (camera, location). Rejects what is not an
 * image, and what stays above the server limit.
 */
export async function compressXray(file: Blob): Promise<CompressedImage> {
  const bitmap = await createImageBitmap(file);
  try {
    const result = await shrinkToTarget(async ({ side, quality }) => {
      const canvas = drawScaled(bitmap, side, '#fff');
      return {
        blob: await encode(canvas, 'image/jpeg', quality),
        width: canvas.width,
        height: canvas.height,
      };
    });
    if (result.blob.size > XRAY_MAX_BYTES) throw new Error('image too large');
    return result;
  } finally {
    bitmap.close();
  }
}
